import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Popover,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EventIcon from '@mui/icons-material/Event';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import TableSkeleton from '../components/common/TableSkeleton';
import UrlaubKalender from '../components/urlaub/UrlaubKalender';
import {
  HinweisOffeneAndereJahre,
  UrlaubFremdJahrChip,
  UrlaubJahrSchalter,
  istAnderesJahr,
  offeneAntraegeNachJahr,
} from '../components/urlaub/UrlaubJahrSchalter';
import AbwesenheitTypPanel from '../components/abwesenheit/AbwesenheitTypPanel';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import { urlaubService } from '../services/urlaubService';
import { krankheitService } from '../services/krankheitService';
import { workationService } from '../services/workationService';
import { kalenderService } from '../services/kalenderService';
import { Urlaub, UrlaubCreate, UrlaubZusammenfassung } from '../types';

type AbwesenheitTyp = 'urlaub' | 'krankheit' | 'workation';
import { berechneArbeitstage, endeNachArbeitstagen, feiertageImZeitraum } from '../utils/arbeitstageBerechnung';

function rundeAufHalbeSchritte(n: number): number {
  if (!Number.isFinite(n)) return 0.5;
  return Math.max(0.5, Math.round(n * 2) / 2);
}

/** Erster Kalendertag ab heute, an dem Von=Bis mindestens einen Arbeitstag hat (kein reines Wochenende/Feiertag). */
function ersterSinnvollerArbeitstagAbHeute(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  for (let i = 0; i < 400; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const key = `${y}-${m}-${day}`;
    if (berechneArbeitstage(key, key) >= 1) return key;
    d.setDate(d.getDate() + 1);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatAnspruchDetails(z: {
  jahresanspruch: number;
  urlaub_uebertrag_auto: number;
  urlaub_uebertrag_manuell: number;
  gesamt: number;
  urlaubstage_voller_anspruch?: number;
  anteilige_monate?: number | null;
  jahresanspruch_anteilig?: boolean;
}): string | undefined {
  const parts: string[] = [];
  if (z.jahresanspruch_anteilig && z.anteilige_monate != null && z.urlaubstage_voller_anspruch) {
    parts.push(
      `${z.jahresanspruch} Jahresanspruch (${z.anteilige_monate}/12 × ${z.urlaubstage_voller_anspruch} Tage)`
    );
  } else {
    parts.push(`${z.jahresanspruch} Jahresanspruch`);
  }
  if (z.urlaub_uebertrag_auto > 0) parts.push(`${z.urlaub_uebertrag_auto} Auto-Übertrag`);
  if (z.urlaub_uebertrag_manuell > 0) parts.push(`${z.urlaub_uebertrag_manuell} manuell`);
  if (parts.length === 1 && z.jahresanspruch === z.gesamt) return undefined;
  return parts.join(' + ');
}

const UrlaubPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(P.URLAUB_APPROVE);
  const canViewAll = hasPermission(P.URLAUB_VIEW_ALL);
  const canKrankheitViewAll = hasPermission(P.KRANKHEIT_VIEW_ALL);
  const canWorkationViewAll = hasPermission(P.WORKATION_VIEW_ALL);
  const canWorkationApprove = hasPermission(P.WORKATION_APPROVE);

  const [abwesenheitTyp, setAbwesenheitTyp] = useState<AbwesenheitTyp>('urlaub');
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meineAntraege, setMeineAntraege] = useState<Urlaub[]>([]);
  const [alleAntraege, setAlleAntraege] = useState<Urlaub[]>([]);
  const [zusammenfassung, setZusammenfassung] = useState<UrlaubZusammenfassung | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [neuDialogOpen, setNeuDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Urlaub | null>(null);
  const [adminActionDialog, setAdminActionDialog] = useState<{
    open: boolean;
    action: 'genehmigen' | 'ablehnen';
    urlaub: Urlaub | null;
  }>({ open: false, action: 'genehmigen', urlaub: null });
  const [adminBemerkung, setAdminBemerkung] = useState('');

  // New request form
  const [formData, setFormData] = useState<UrlaubCreate>({
    von: '',
    bis: '',
    anzahl_tage: 0,
    bemerkung: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [feiertagInfo, setFeiertagInfo] = useState<Array<{ datum: string; name: string }>>([]);
  const [neuDialogSummary, setNeuDialogSummary] = useState<UrlaubZusammenfassung | null>(null);

  // iCal-Abo (persoenlicher Feed inkl. eigenem genehmigten Urlaub)
  const [icalAnchor, setIcalAnchor] = useState<HTMLElement | null>(null);
  const [icalUrl, setIcalUrl] = useState<string | null>(null);
  const [icalLoading, setIcalLoading] = useState(false);
  const [icalTyp, setIcalTyp] = useState<'urlaub' | 'kombiniert'>('urlaub');

  const icalDisplayUrl = useMemo(() => {
    if (!icalUrl) return null;
    return icalTyp === 'urlaub' ? `${icalUrl}&typ=urlaub` : icalUrl;
  }, [icalUrl, icalTyp]);

  const handleIcalOpen = async (e: React.MouseEvent<HTMLElement>) => {
    setIcalAnchor(e.currentTarget);
    if (!icalUrl) {
      setIcalLoading(true);
      try {
        const { url } = await kalenderService.getIcalUrl();
        setIcalUrl(url);
      } finally {
        setIcalLoading(false);
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const dialogAntragJahr = useMemo(() => {
    if (formData.von && formData.von.length >= 4) {
      const y = parseInt(formData.von.slice(0, 4), 10);
      if (!Number.isNaN(y)) return y;
    }
    return currentYear;
  }, [formData.von, currentYear]);

  useEffect(() => {
    if (!neuDialogOpen) return;
    let cancelled = false;
    urlaubService.zusammenfassung(dialogAntragJahr).then((s) => {
      if (!cancelled) setNeuDialogSummary(s);
    });
    return () => {
      cancelled = true;
    };
  }, [neuDialogOpen, dialogAntragJahr]);

  useEffect(() => {
    if (!neuDialogOpen) return;
    const { von, bis } = formData;
    if (von && bis && bis >= von) {
      setFeiertagInfo(feiertageImZeitraum(von, bis));
    } else {
      setFeiertagInfo([]);
    }
  }, [neuDialogOpen, formData.von, formData.bis]);

  const resetNeuForm = useCallback(() => {
    setFormData({ von: '', bis: '', anzahl_tage: 0, bemerkung: '' });
    setFormError(null);
    setFeiertagInfo([]);
    setNeuDialogSummary(null);
  }, []);

  /** Dialog öffnen: Von/Bis auf nächsten sinnvollen Arbeitstag, einheitliche Steuerung nur über +/− (kein natives Spinner-Duplikat). */
  const initNeuFormMitStandard = useCallback(() => {
    const start = ersterSinnvollerArbeitstagAbHeute();
    const tage = berechneArbeitstage(start, start);
    setFormData({ von: start, bis: start, anzahl_tage: tage, bemerkung: '' });
    setFormError(null);
    setFeiertagInfo(feiertageImZeitraum(start, start));
    setNeuDialogSummary(null);
  }, []);

  const handleNeuDialogClose = useCallback(() => {
    setNeuDialogOpen(false);
    resetNeuForm();
  }, [resetNeuForm]);

  const handleVonChange = (value: string) => {
    setFormData((prev) => {
      if (!value) {
        return { ...prev, von: '', bis: '', anzahl_tage: 0 };
      }
      let nextBis = prev.bis;
      if (!nextBis || nextBis < value) {
        nextBis = value;
      }
      const anzahl = berechneArbeitstage(value, nextBis);
      return { ...prev, von: value, bis: nextBis, anzahl_tage: anzahl };
    });
  };

  const handleBisChange = (value: string) => {
    setFormData((prev) => {
      const von = prev.von;
      if (!value) {
        return { ...prev, bis: '', anzahl_tage: 0 };
      }
      if (!von) {
        return { ...prev, bis: value };
      }
      if (value < von) {
        const anzahl = berechneArbeitstage(von, von);
        return { ...prev, bis: von, anzahl_tage: anzahl };
      }
      return { ...prev, bis: value, anzahl_tage: berechneArbeitstage(von, value) };
    });
  };

  const handleAnzahlFeldChange = (raw: string) => {
    setFormData((prev) => {
      if (!prev.von) {
        if (raw === '' || raw === '-') return { ...prev, anzahl_tage: 0 };
        return prev;
      }
      if (raw === '' || raw === '-') {
        return { ...prev, anzahl_tage: 0 };
      }
      const n = parseFloat(raw.replace(',', '.'));
      if (Number.isNaN(n)) return prev;
      const anzahl = rundeAufHalbeSchritte(n);
      const bis = endeNachArbeitstagen(prev.von, anzahl);
      if (!bis) return { ...prev, anzahl_tage: anzahl };
      return { ...prev, anzahl_tage: anzahl, bis };
    });
  };

  const handleAnzahlStep = (delta: number) => {
    setFormData((prev) => {
      const von = prev.von;
      if (!von) return prev;
      const next = rundeAufHalbeSchritte(prev.anzahl_tage + delta);
      const bis = endeNachArbeitstagen(von, next);
      if (!bis) return { ...prev, anzahl_tage: next };
      return { ...prev, anzahl_tage: next, bis };
    });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listenOptionen = { offeneAndereJahre: true };
      const [meine, summary] = await Promise.all([
        urlaubService.list(selectedYear, listenOptionen),
        urlaubService.zusammenfassung(selectedYear),
      ]);
      setMeineAntraege(meine);
      setZusammenfassung(summary);

      if (canViewAll) {
        const alle = await urlaubService.listAlle(selectedYear, undefined, listenOptionen);
        setAlleAntraege(alle);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden der Daten';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, canViewAll]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateSubmit = async () => {
    setFormError(null);
    if (!formData.von || !formData.bis) {
      setFormError('Bitte Von- und Bis-Datum angeben');
      return;
    }
    if (formData.bis < formData.von) {
      setFormError('Das Enddatum darf nicht vor dem Startdatum liegen');
      return;
    }
    const erwartet = berechneArbeitstage(formData.von, formData.bis);
    if (erwartet === 0) {
      setFormError('Im gewählten Zeitraum liegen keine Arbeitstage');
      return;
    }
    if (formData.anzahl_tage < 0.5) {
      setFormError('Anzahl Tage muss mindestens 0,5 betragen');
      return;
    }
    if (formData.anzahl_tage > erwartet) {
      setFormError(
        `Anzahl Tage (${formData.anzahl_tage}) ist ungültig für den Zeitraum (max. ${erwartet} Arbeitstage)`
      );
      return;
    }
    if (neuDialogSummary && formData.anzahl_tage > neuDialogSummary.verbleibend + 1e-9) {
      setFormError(
        `Nicht genügend Resturlaub: ${neuDialogSummary.verbleibend.toFixed(1)} Tage verfügbar, ` +
          `${formData.anzahl_tage.toFixed(1)} Tage beantragt`
      );
      return;
    }

    try {
      await urlaubService.create(formData);
      setNeuDialogOpen(false);
      resetNeuForm();
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setFormError(axiosErr?.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await urlaubService.remove(deleteTarget.id);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleAdminAction = async () => {
    if (!adminActionDialog.urlaub) return;
    try {
      if (adminActionDialog.action === 'genehmigen') {
        await urlaubService.genehmigen(adminActionDialog.urlaub.id, adminBemerkung);
      } else {
        await urlaubService.ablehnen(adminActionDialog.urlaub.id, adminBemerkung);
      }
      setAdminActionDialog({ open: false, action: 'genehmigen', urlaub: null });
      setAdminBemerkung('');
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr?.response?.data?.detail || 'Fehler bei der Aktion');
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'Genehmigt':
        return <Chip label="Genehmigt" size="small" sx={{ bgcolor: '#1b5e20', color: '#fff', fontWeight: 600 }} />;
      case 'Abgelehnt':
        return <Chip label="Abgelehnt" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontWeight: 600 }} />;
      case 'Beantragt':
      default:
        return <Chip label="Beantragt" size="small" sx={{ bgcolor: '#e65100', color: '#fff', fontWeight: 600 }} />;
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const erwarteteArbeitstage = useMemo(() => {
    if (!formData.von || !formData.bis || formData.bis < formData.von) return 0;
    return berechneArbeitstage(formData.von, formData.bis);
  }, [formData.von, formData.bis]);

  const restNachGenehmigung = useMemo(() => {
    if (!neuDialogSummary || formData.anzahl_tage <= 0) return null;
    return neuDialogSummary.verbleibend - formData.anzahl_tage;
  }, [neuDialogSummary, formData.anzahl_tage]);

  const beantragenDeaktiviert = useMemo(() => {
    if (!formData.von || !formData.bis || formData.bis < formData.von) return true;
    if (formData.anzahl_tage < 0.5) return true;
    if (erwarteteArbeitstage === 0) return true;
    if (formData.anzahl_tage > erwarteteArbeitstage) return true;
    if (neuDialogSummary && formData.anzahl_tage > neuDialogSummary.verbleibend + 1e-9) return true;
    return false;
  }, [
    formData.von,
    formData.bis,
    formData.anzahl_tage,
    erwarteteArbeitstage,
    neuDialogSummary,
  ]);

  const accent = theme.palette.primary.main;
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? '#1e2240' : '#ffffff';
  const cardBorder = isDark ? '#2c3358' : '#e0e0e0';

  const summaryCards = zusammenfassung
    ? [
        {
          label: 'Gesamturlaub',
          value: zusammenfassung.gesamt,
          subtitle:
            formatAnspruchDetails(zusammenfassung),
          icon: <BeachAccessIcon sx={{ fontSize: 32 }} />,
          color: accent,
        },
        {
          label: 'Beantragt',
          value: zusammenfassung.beantragt,
          icon: <HourglassEmptyIcon sx={{ fontSize: 32 }} />,
          color: '#e65100',
        },
        {
          label: 'Genehmigt',
          value: zusammenfassung.genehmigt,
          icon: <FlightTakeoffIcon sx={{ fontSize: 32 }} />,
          color: '#1b5e20',
        },
        {
          label: 'Verbleibend',
          value: zusammenfassung.verbleibend,
          icon: <EventIcon sx={{ fontSize: 32 }} />,
          color: zusammenfassung.verbleibend > 5 ? '#1565c0' : '#b71c1c',
        },
      ]
    : [];

  const meineKalenderItems = useMemo(
    () =>
      meineAntraege
        .filter((u) => !istAnderesJahr(u.von, selectedYear))
        .map((u) => ({
          id: u.id,
          label: u.bemerkung?.trim() || 'Urlaub',
          von: u.von,
          bis: u.bis,
          status: u.status,
          anzahl_tage: u.anzahl_tage,
          bemerkung: u.bemerkung,
        })),
    [meineAntraege, selectedYear]
  );

  const eigeneHinweisGruppen = offeneAntraegeNachJahr(meineAntraege, selectedYear);
  const alleHinweisGruppen = offeneAntraegeNachJahr(alleAntraege, selectedYear);
  const hinweisGruppen = canViewAll ? alleHinweisGruppen : eigeneHinweisGruppen;

  const handleHinweisJahr = (jahr: number) => {
    setSelectedYear(jahr);
    if (!canViewAll) return;
    const eigene = eigeneHinweisGruppen.find((g) => g.jahr === jahr)?.anzahl ?? 0;
    const alle = alleHinweisGruppen.find((g) => g.jahr === jahr)?.anzahl ?? 0;
    if (alle > eigene) setTabIndex(2);
  };

  const fremdZeileSx = (von: string) =>
    istAnderesJahr(von, selectedYear) ? { bgcolor: 'rgba(230, 81, 0, 0.08)' } : undefined;

  return (
    <Box>
      <PageHeader
        title="Abwesenheiten"
        subtitle={
          abwesenheitTyp === 'urlaub' ? (
            <UrlaubJahrSchalter
              jahr={selectedYear}
              aktuellesJahr={currentYear}
              onChange={setSelectedYear}
            />
          ) : (
            `${abwesenheitTyp === 'krankheit' ? 'Krankheit' : 'Workation'} · ${currentYear}`
          )
        }
        breadcrumbs={[{ label: 'Abwesenheiten' }]}
        onAdd={
          abwesenheitTyp === 'urlaub'
            ? () => {
                initNeuFormMitStandard();
                setNeuDialogOpen(true);
              }
            : undefined
        }
        addLabel={abwesenheitTyp === 'urlaub' ? 'Urlaub beantragen' : undefined}
      />

      <Tabs
        value={abwesenheitTyp}
        onChange={(_, v: AbwesenheitTyp) => setAbwesenheitTyp(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="urlaub" label="Urlaub" />
        <Tab value="krankheit" label="Krankheit" />
        <Tab value="workation" label="Workation" />
      </Tabs>

      {abwesenheitTyp === 'krankheit' && (
        <AbwesenheitTypPanel
          typLabel="Krankheit"
          hinweis="Krankmeldung ohne Genehmigung und ohne Kontingent. Bitte keine Diagnosen eintragen."
          editableStatuses={['Gemeldet']}
          deletableStatuses={['Gemeldet']}
          canViewAll={canKrankheitViewAll}
          createButtonLabel="Krankheit melden"
          loadEigene={(jahr) => krankheitService.list(jahr)}
          loadAlle={(jahr) => krankheitService.listAlle(jahr)}
          create={(data) => krankheitService.create(data)}
          remove={(id) => krankheitService.remove(id)}
        />
      )}

      {abwesenheitTyp === 'workation' && (
        <AbwesenheitTypPanel
          typLabel="Workation"
          hinweis="Workation erfordert die Einwilligung des Arbeitgebers und hat kein Kontingent. Du bleibst voll arbeitsfähig, bist aber örtlich nicht einsetzbar."
          editableStatuses={['Beantragt']}
          deletableStatuses={['Beantragt']}
          canViewAll={canWorkationViewAll}
          canApprove={canWorkationApprove}
          createButtonLabel="Workation beantragen"
          loadEigene={(jahr) => workationService.list(jahr)}
          loadAlle={(jahr) => workationService.listAlle(jahr)}
          create={(data) => workationService.create(data)}
          remove={(id) => workationService.remove(id)}
          genehmigen={(id, b) => workationService.genehmigen(id, b)}
          ablehnen={(id, b) => workationService.ablehnen(id, b)}
        />
      )}

      {abwesenheitTyp === 'urlaub' && (
        <HinweisOffeneAndereJahre gruppen={loading ? [] : hinweisGruppen} onJahr={handleHinweisJahr} />
      )}

      {abwesenheitTyp === 'urlaub' && error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {abwesenheitTyp === 'urlaub' && zusammenfassung && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          {summaryCards.map((card) => (
            <Card
              key={card.label}
              sx={{
                bgcolor: cardBg,
                border: `1px solid ${cardBorder}`,
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ color: card.color, opacity: 0.85 }}>{card.icon}</Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: card.color }}>
                    {card.value}
                    <Typography component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary', ml: 0.5 }}>
                      Tage
                    </Typography>
                  </Typography>
                  {'subtitle' in card && card.subtitle && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                      {card.subtitle}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Urlaub Tabs */}
      {abwesenheitTyp === 'urlaub' && (
      <Card sx={{ bgcolor: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: `1px solid ${cardBorder}`, px: 2 }}
        >
          <Tab label="Meine Anträge" />
          <Tab label="Kalender" />
          {canViewAll && <Tab label="Alle Anträge" />}
        </Tabs>

        {/* Tab: Meine Antraege */}
        {tabIndex === 0 && (
          <TableContainer>
            {loading ? (
              <TableSkeleton columns={6} rows={5} />
            ) : meineAntraege.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Keine Urlaubsanträge für {selectedYear}</Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Von</TableCell>
                    <TableCell>Bis</TableCell>
                    <TableCell>Tage</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Bemerkung</TableCell>
                    <TableCell>Admin-Bemerkung</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meineAntraege.map((u) => (
                    <TableRow key={u.id} hover sx={fremdZeileSx(u.von)}>
                      <TableCell>
                        {formatDate(u.von)}
                        <UrlaubFremdJahrChip von={u.von} selectedYear={selectedYear} />
                      </TableCell>
                      <TableCell>{formatDate(u.bis)}</TableCell>
                      <TableCell>{u.anzahl_tage}</TableCell>
                      <TableCell>{getStatusChip(u.status)}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.bemerkung || '-'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.admin_bemerkung || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {u.status === 'Beantragt' && (
                          <Tooltip title="Antrag löschen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setDeleteTarget(u);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}

        {/* Tab: Kalender (eigener Urlaub) */}
        {tabIndex === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarMonthIcon />}
                onClick={handleIcalOpen}
              >
                Kalender abonnieren (iCal)
              </Button>
            </Box>
            {loading ? (
              <TableSkeleton columns={1} rows={6} />
            ) : (
              <UrlaubKalender
                items={meineKalenderItems}
                selectedYear={selectedYear}
                isDark={isDark}
                emptyHint={`Keine eigenen Urlaubsanträge für ${selectedYear} vorhanden`}
              />
            )}
          </Box>
        )}

        {/* Tab: Alle Antraege (Admin) */}
        {tabIndex === 2 && canViewAll && (
          <TableContainer>
            {loading ? (
              <TableSkeleton columns={8} rows={5} />
            ) : alleAntraege.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Keine Urlaubsanträge für {selectedYear}</Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Mitarbeiter</TableCell>
                    <TableCell>Von</TableCell>
                    <TableCell>Bis</TableCell>
                    <TableCell>Tage</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Bemerkung</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alleAntraege.map((u) => (
                    <TableRow key={u.id} hover sx={fremdZeileSx(u.von)}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {u.mitarbeiter
                          ? `${u.mitarbeiter.vorname} ${u.mitarbeiter.nachname}`
                          : `ID ${u.mitarbeiter_id}`}
                      </TableCell>
                      <TableCell>
                        {formatDate(u.von)}
                        <UrlaubFremdJahrChip von={u.von} selectedYear={selectedYear} />
                      </TableCell>
                      <TableCell>{formatDate(u.bis)}</TableCell>
                      <TableCell>{u.anzahl_tage}</TableCell>
                      <TableCell>{getStatusChip(u.status)}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.bemerkung || '-'}
                      </TableCell>
                      <TableCell align="right">
                        {u.status === 'Beantragt' && canApprove && u.mitarbeiter_id !== user?.id && (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Genehmigen">
                              <IconButton
                                size="small"
                                sx={{ color: '#1b5e20' }}
                                onClick={() =>
                                  setAdminActionDialog({
                                    open: true,
                                    action: 'genehmigen',
                                    urlaub: u,
                                  })
                                }
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ablehnen">
                              <IconButton
                                size="small"
                                sx={{ color: '#b71c1c' }}
                                onClick={() =>
                                  setAdminActionDialog({
                                    open: true,
                                    action: 'ablehnen',
                                    urlaub: u,
                                  })
                                }
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}
      </Card>
      )}

      {/* Popover: iCal-Abo-URL */}
      {abwesenheitTyp === 'urlaub' && (
      <Popover
        open={Boolean(icalAnchor)}
        anchorEl={icalAnchor}
        onClose={() => setIcalAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, mt: 1, borderRadius: 3, minWidth: 420 } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Kalender abonnieren (iCal)
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          Diese persönliche URL in Google Calendar, Outlook oder Apple Kalender als Abo hinzufügen.
          Der Feed wird automatisch aktualisiert.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Chip
            label="Nur Urlaub"
            size="small"
            color={icalTyp === 'urlaub' ? 'primary' : 'default'}
            variant={icalTyp === 'urlaub' ? 'filled' : 'outlined'}
            onClick={() => setIcalTyp('urlaub')}
          />
          <Chip
            label="Audits + Urlaub"
            size="small"
            color={icalTyp === 'kombiniert' ? 'primary' : 'default'}
            variant={icalTyp === 'kombiniert' ? 'filled' : 'outlined'}
            onClick={() => setIcalTyp('kombiniert')}
          />
        </Box>
        {icalLoading ? (
          <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
        ) : icalDisplayUrl ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={icalDisplayUrl}
              inputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
            />
            <Tooltip title="URL kopieren">
              <IconButton onClick={() => navigator.clipboard.writeText(icalDisplayUrl)} color="primary">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Popover>
      )}

      {/* Dialog: Neuen Urlaub beantragen */}
      <Dialog open={neuDialogOpen} onClose={handleNeuDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Neuen Urlaub beantragen</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {neuDialogSummary ? (
              <Alert severity="info" sx={{ py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Kontingent {dialogAntragJahr}
                </Typography>
                <Typography variant="body2">
                  {formatAnspruchDetails(neuDialogSummary) || `${neuDialogSummary.jahresanspruch} Jahresanspruch`}
                  {' · '}Zustehend gesamt: {neuDialogSummary.gesamt.toFixed(1)} Tage · Bereits genehmigt:{' '}
                  {neuDialogSummary.genehmigt.toFixed(1)} · Offene Anträge:{' '}
                  {neuDialogSummary.beantragt.toFixed(1)} (im Rest noch nicht abgezogen)
                </Typography>
                {neuDialogSummary.jahresanspruch_anteilig && neuDialogSummary.anteilige_monate != null && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Anteilig im Eintrittsjahr: {neuDialogSummary.anteilige_monate} volle Monate à 1/12 von{' '}
                    {neuDialogSummary.urlaubstage_voller_anspruch} Tagen
                  </Typography>
                )}
                {neuDialogSummary.uebertrag_hinweis_frist_am && neuDialogSummary.urlaub_uebertrag_auto > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5, color: neuDialogSummary.uebertrag_hinweis_frist_ueberschritten ? 'warning.main' : 'text.secondary' }}>
                    {neuDialogSummary.uebertrag_hinweis_frist_ueberschritten
                      ? `Planungs-Hinweisfrist (${formatDate(neuDialogSummary.uebertrag_hinweis_frist_am)}) überschritten — der Übertrag bleibt im Anspruch enthalten.`
                      : `Planungs-Hinweis: Vorjahres-Übertrag bis ${formatDate(neuDialogSummary.uebertrag_hinweis_frist_am)} einplanen`}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Aktueller Rest vor diesem Antrag:{' '}
                  <strong>{neuDialogSummary.verbleibend.toFixed(1)} Tage</strong>
                </Typography>
                {restNachGenehmigung !== null && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      fontWeight: 600,
                      color: restNachGenehmigung < 0 ? 'error.main' : 'text.primary',
                    }}
                  >
                    Voraussichtlicher Rest nach Genehmigung dieses Antrags:{' '}
                    {restNachGenehmigung.toFixed(1)} Tage
                  </Typography>
                )}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Kontingent wird geladen …
              </Typography>
            )}
            {restNachGenehmigung !== null && restNachGenehmigung < 0 && (
              <Alert severity="warning">
                Beantragte Tage übersteigen den verbleibenden Urlaub. Bitte Anzahl oder Zeitraum
                anpassen.
              </Alert>
            )}
            <TextField
              label="Von"
              type="date"
              value={formData.von}
              onChange={(e) => handleVonChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Bis"
              type="date"
              value={formData.bis}
              onChange={(e) => handleBisChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Anzahl Arbeitstage"
              type="number"
              value={formData.anzahl_tage === 0 ? '' : formData.anzahl_tage}
              onChange={(e) => handleAnzahlFeldChange(e.target.value)}
              disabled={!formData.von}
              inputProps={{ min: 0.5, step: 0.5 }}
              fullWidth
              required
              sx={{
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Halben Tag abziehen"
                      disabled={!formData.von || formData.anzahl_tage <= 0.5}
                      onClick={() => handleAnzahlStep(-0.5)}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Halben Tag hinzufügen"
                      disabled={!formData.von}
                      onClick={() => handleAnzahlStep(0.5)}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              helperText={
                !formData.von
                  ? 'Bitte zuerst ein Von-Datum wählen.'
                  : formData.von && formData.bis && formData.bis >= formData.von
                    ? `Automatisch berechnet (ohne Wochenenden & NRW-Feiertage). Max. ${erwarteteArbeitstage} Arbeitstage im Zeitraum. Halbe Tage (0,5) per Eingabe oder +/−.`
                    : 'Wird automatisch berechnet, wenn Von- und Bis-Datum gesetzt sind'
              }
            />
            {feiertagInfo.length > 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Feiertage im Zeitraum (nicht mitgezählt):
                </Typography>
                {feiertagInfo.map((f) => (
                  <Typography key={f.datum} variant="body2">
                    {new Date(f.datum + 'T00:00:00').toLocaleDateString('de-DE', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}{' '}
                    – {f.name}
                  </Typography>
                ))}
              </Alert>
            )}
            <TextField
              label="Bemerkung"
              value={formData.bemerkung || ''}
              onChange={(e) => setFormData({ ...formData, bemerkung: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleNeuDialogClose}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={beantragenDeaktiviert}>
            Beantragen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Urlaubsantrag löschen"
        message={
          deleteTarget
            ? `Möchten Sie den Urlaubsantrag vom ${formatDate(deleteTarget.von)} bis ${formatDate(deleteTarget.bis)} (${deleteTarget.anzahl_tage} Tage) wirklich löschen?`
            : ''
        }
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeleteTarget(null);
        }}
      />

      {/* Admin Action Dialog (Genehmigen/Ablehnen) */}
      <Dialog
        open={adminActionDialog.open}
        onClose={() => {
          setAdminActionDialog({ open: false, action: 'genehmigen', urlaub: null });
          setAdminBemerkung('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {adminActionDialog.action === 'genehmigen' ? 'Urlaub genehmigen' : 'Urlaub ablehnen'}
        </DialogTitle>
        <DialogContent>
          {adminActionDialog.urlaub && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Mitarbeiter:</strong>{' '}
                {adminActionDialog.urlaub.mitarbeiter
                  ? `${adminActionDialog.urlaub.mitarbeiter.vorname} ${adminActionDialog.urlaub.mitarbeiter.nachname}`
                  : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Zeitraum:</strong>{' '}
                {formatDate(adminActionDialog.urlaub.von)} - {formatDate(adminActionDialog.urlaub.bis)}{' '}
                ({adminActionDialog.urlaub.anzahl_tage} Tage)
              </Typography>
              {adminActionDialog.urlaub.bemerkung && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Bemerkung:</strong> {adminActionDialog.urlaub.bemerkung}
                </Typography>
              )}
            </Box>
          )}
          <TextField
            label="Admin-Bemerkung (optional)"
            value={adminBemerkung}
            onChange={(e) => setAdminBemerkung(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAdminActionDialog({ open: false, action: 'genehmigen', urlaub: null });
              setAdminBemerkung('');
            }}
          >
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color={adminActionDialog.action === 'genehmigen' ? 'success' : 'error'}
            onClick={handleAdminAction}
          >
            {adminActionDialog.action === 'genehmigen' ? 'Genehmigen' : 'Ablehnen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UrlaubPage;
